/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable react-hooks/exhaustive-deps */
"use client";

import { useEffect, useState } from "react";
import { useRouter } from "next/navigation";
import { sdk } from "@/lib/medusa/client";
import { decodeToken } from "react-jwt";
import { Locales } from "@/lib/i18n";
import { __, getPermalink, getRedirectPath } from "@/lib/i18n/functions";
import { useCustomer } from "@/hooks/useCustomer";
import { useCompanyDetails } from "@/queries/company";
import { toast } from "@/components/atoms/toast";
import { Loader } from "lucide-react";
import { Button } from "@/components/atoms/buttons";
import Link from "next/link";
import { Graphics } from "@/components/graphics";
import { useCheckoutStore } from "@/stores/checkout-store";
import { useQueryClient } from "@tanstack/react-query";

type GoogleRedirectCompoProps = {
  locale: Locales;
};

interface InvitationData {
  token: string;
  email: string;
  company_name: string;
  timestamp: number;
}

export default function GoogleRedirectCompo({ locale }: GoogleRedirectCompoProps) {
  const [loading, setLoading] = useState(true);
  const [hasError, setHasError] = useState(false);
  const [invitationData, setInvitationData] = useState<InvitationData | null>(null);
  const router = useRouter();
  const { customer, setCustomer } = useCustomer();
  const queryClient = useQueryClient();

  const { data: companyDetails } = useCompanyDetails(invitationData?.company_name || "");

  useEffect(() => {
    if (typeof window !== "undefined") {
      const storedData = sessionStorage.getItem("invitation_data");
      if (storedData) {
        try {
          const parsedData: InvitationData = JSON.parse(storedData);
          const oneHour = 60 * 60 * 1000;
          if (Date.now() - parsedData.timestamp < oneHour) {
            setInvitationData(parsedData);
          } else {
            // Remove expired data
            sessionStorage.removeItem("invitation_data");
          }
        } catch (error) {
          console.error("Error parsing invitation data:", error);
          sessionStorage.removeItem("invitation_data");
        }
      }
    }
  }, []);

  async function loginGoogle(token: unknown) {
    try {
      if (typeof window !== "undefined") {
        localStorage.setItem("medusa_auth_token", token as string);
      }
    } catch (error: any) {
      return error.toString();
    }
  }

  async function getUserMeta(auth_identity_id: string) {
    try {
      const response = await fetch(
        `${process.env.NEXT_PUBLIC_MEDUSA_BACKEND_URL}/store/user-meta/${auth_identity_id}`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            "x-publishable-api-key": process.env.NEXT_PUBLIC_MEDUSA_PUBLISHABLE_KEY!,
          },
        }
      );

      if (!response.ok) throw new Error("Failed to fetch user metadata");

      const data = await response.json();
      return data;
    } catch (err) {
      console.error("Error fetching user meta:", err);
      return null;
    }
  }

  async function processInvitation(customerId: string, inviteData: InvitationData) {
    try {
      if (companyDetails?.id) {
        await sdk.client.fetch(`store/companies/${companyDetails.id}/invite/process`, {
          method: "POST",
          body: {
            token: inviteData.token,
            accepted: true,
            customer_id: customerId,
          },
        });

        if (typeof window !== "undefined") {
          sessionStorage.removeItem("invitation_data");
        }
      }
    } catch (error) {
      console.error("Error processing invitation:", error);
      throw error;
    }
  }

  const validateCallback = async (queryParams: Record<string, string>) => {
    try {
      const token = await sdk.auth.callback("customer", "google", queryParams);
      sdk.client.setToken(token);

      const decodedToken = decodeToken(token) as {
        actor_id: string;
        auth_identity_id: string;
      };

      if (!decodedToken || !decodedToken.auth_identity_id) {
        throw new Error("Invalid or expired token. Missing auth_identity_id.");
      }

      const shouldCreateCustomer = !decodedToken.actor_id;

      const userMetaData = await getUserMeta(decodedToken!.auth_identity_id);

      if (!userMetaData || !userMetaData.email) {
        throw new Error("User metadata is missing or invalid.");
      }

      let customerId: string;

      if (shouldCreateCustomer) {
        const { customer: createdCustomer } = await sdk.store.customer.create(
          {
            email: userMetaData.email,
            first_name: userMetaData.first_name,
            last_name: userMetaData.last_name,
            company_name: "",
            metadata: {
              ...userMetaData,
              verified: true,
            },
          },
          {},
          {}
        );

        setCustomer(createdCustomer);
        customerId = createdCustomer.id;
        await loginGoogle(token);
        await sdk.auth.refresh();
      } else {
        await loginGoogle(token);
        const currentCustomerResponse = await sdk.store.customer.retrieve();
        setCustomer(currentCustomerResponse.customer);
        customerId = currentCustomerResponse.customer.id;
      }

      useCheckoutStore.getState().resetFormData();
      queryClient.removeQueries({ queryKey: ["company-details"] });
      queryClient.removeQueries({ queryKey: ["company-employees"] });
      queryClient.removeQueries({ queryKey: ["company-addresses"] });

      if (invitationData && invitationData.token && companyDetails && companyDetails.id) {
        try {
          await processInvitation(customerId, invitationData);
        } catch (inviteError) {
          console.error("Error processing invitation:", inviteError);
          toast.warning({
            title: __("Account created successfully, but failed to process company invitation.", locale),
          });
        }
      }

      const redirectPath = getRedirectPath(locale);
      router.push(redirectPath);
    } catch (err) {
      console.warn("Customer logged in err:", err);
      setHasError(true);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (loading) {
      const searchParams = new URLSearchParams(window.location.search);
      const queryParams = Object.fromEntries(searchParams.entries());
      validateCallback(queryParams);
    }
  }, [loading]);

  return (
    <div className="container flex min-h-[50vh] items-center justify-center px-4 py-16">
      {loading && (
        <div className="text-dark-primary flex items-center gap-2">
          <Loader className="animate-spin" />
          <p className="text-lg">{__("Logging you in via Google...", locale)}</p>
        </div>
      )}
      {customer && (
        <div className="text-dark-primary flex items-center gap-2">
          <Loader className="animate-spin" />
          <p className="text-lg">{__("Redirecting you to your account...", locale)}</p>
        </div>
      )}
      {hasError && (
        <section
          aria-labelledby="google-login-error-title"
          className="text-dark-primary mx-auto flex max-w-[34rem] flex-col items-center gap-5 text-center"
          role="alert"
        >
          <div className="flex size-12 items-center justify-center rounded-full bg-red-50">
            <Graphics.errorIcon />
          </div>
          <div className="space-y-2">
            <h1 id="google-login-error-title" className="font-cabinet-grotesk text-2xl font-bold">
              {__("Google login failed", locale)}
            </h1>
            <p className="text-primary-light leading-h1">
              {__("We couldn't complete your Google login. Please try again or use your email and password.", locale)}
            </p>
          </div>
          <Button asChild size="md">
            <Link href={getPermalink(locale, "login")}>{__("Back to login", locale)}</Link>
          </Button>
        </section>
      )}
    </div>
  );
}
